import {
  MAX_PLAYSTYLES,
  MAX_PLAYSTYLES_PLUS,
  PlayStylesLabRecommendation,
  playStylesLabSelector,
} from "@/redux/playStylesLab/playStylesLabSlice";
import { playersSelector } from "@/redux/players/playersSlice";
import { useTranslation } from "@repo/shared/i18n";
import { includesIgnoringAccents } from "@repo/shared/utils";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { PlayStyleCard } from "./PlayStyleCard";

type Props = {
  playStylesPlus: PlayStylesLabRecommendation[];
  playStyles: PlayStylesLabRecommendation[];
  labPlayStyles: number[];
  labPlayStylesPlus: number[];
  // The base player's innate PlayStyles, counted against the caps alongside the
  // applied ones.
  basePlayStyles: number[];
  basePlayStylesPlus: number[];
  searchTerm?: string;
  onClickPlayStyle?: (item: PlayStylesLabRecommendation) => void;
};

const idOf = (item: PlayStylesLabRecommendation) =>
  item.playStylesPlus.length > 0 ? item.playStylesPlus[0] : item.playStyles[0];

const Grid = ({
  label,
  items,
  appliedIds,
  baseIds,
  crossAppliedIds,
  max,
  nameOf,
  onClickPlayStyle,
}: {
  label: string;
  items: PlayStylesLabRecommendation[];
  appliedIds: number[];
  baseIds: number[];
  crossAppliedIds: number[];
  max: number;
  nameOf: (id: number) => string;
  onClickPlayStyle?: (item: PlayStylesLabRecommendation) => void;
}) => {
  const { status } = useSelector(playStylesLabSelector);
  const capReached = new Set([...baseIds, ...appliedIds]).size >= max;
  if (items.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium text-dark-tertiary">{label}</p>
      <div className="flex flex-col gap-y-2 desktop:gap-4 desktop:grid desktop:grid-cols-3 large-desktop:grid-cols-4">
        {items.map((item) => {
          const id = idOf(item);
          const selected = appliedIds.includes(id);
          return (
            <PlayStyleCard
              key={`${item.playStylesPlus.length > 0 ? "plus-" : ""}${id}`}
              item={item}
              name={nameOf(id)}
              selected={selected}
              crossApplied={crossAppliedIds.includes(id)}
              disabled={!selected && (capReached || status === "pending")}
              onSelect={onClickPlayStyle}
            />
          );
        })}
      </div>
    </div>
  );
};

export const PlayStylesLabSteps = ({
  playStylesPlus,
  playStyles,
  labPlayStyles,
  labPlayStylesPlus,
  basePlayStyles,
  basePlayStylesPlus,
  searchTerm,
  onClickPlayStyle,
}: Props) => {
  const { playStylesDict } = useSelector(playersSelector);
  const nameOf = (id: number) => playStylesDict?.[id]?.name ?? "";

  const matches = (item: PlayStylesLabRecommendation) =>
    !searchTerm || includesIgnoringAccents(nameOf(idOf(item)), searchTerm);

  const filteredPlus = useMemo(
    () => playStylesPlus.filter(matches),
    [playStylesPlus, searchTerm, playStylesDict],
  );
  const filtered = useMemo(
    () => playStyles.filter(matches),
    [playStyles, searchTerm, playStylesDict],
  );

  const { t } = useTranslation("players");
  return (
    <div className="flex flex-col gap-8 pb-8">
      <Grid
        label={t("playStylesLab.playStylesPlus")}
        items={filteredPlus}
        appliedIds={labPlayStylesPlus}
        baseIds={basePlayStylesPlus}
        crossAppliedIds={labPlayStyles}
        max={MAX_PLAYSTYLES_PLUS}
        nameOf={nameOf}
        onClickPlayStyle={onClickPlayStyle}
      />
      <Grid
        label={t("playStylesLab.playStyles")}
        items={filtered}
        appliedIds={labPlayStyles}
        baseIds={basePlayStyles}
        crossAppliedIds={labPlayStylesPlus}
        max={MAX_PLAYSTYLES}
        nameOf={nameOf}
        onClickPlayStyle={onClickPlayStyle}
      />
    </div>
  );
};
